// domain/services/programDefaults.js

import { TARGET_DATA_TYPE_META } from '../constants/targetDataType';

/**
 * What a program's recording defaults should be, given a set of targets.
 *
 * A PROGRAM'S METRIC IS A DEFAULT, NOT A CONSTRAINT: a target may override it, and an unset target
 * reads the program's where it suits the target's type. So when every target wants the same metric
 * it is said once on the program, and the next target a clinician adds inherits it too. Where they
 * disagree there is no single
 * honest answer, so each target carries its own and the program's is left to whoever has an
 * opinion — picking the majority would invent a decision nobody made.
 *
 * The failure this exists to prevent is silent: a duration target inheriting percent correct
 * summarises a stopwatch as a success rate, and looks like nothing worse than an odd graph.
 *
 * @typedef {{ id: string, dataType?: string, primaryMetric?: string|null }} TargetLike
 */

/**
 * @param {TargetLike} target
 * @param {string|null} programMetric
 * @returns {boolean} the target has no metric of its own and will read the program's
 */
function inheritsProgramMetric(target, programMetric) {
  return !target?.primaryMetric && programMetric !== null
    && Boolean(TARGET_DATA_TYPE_META[target?.dataType]?.passThroughMetrics?.includes(programMetric));
} // inheritsProgramMetric()

/**
 * The metric a target wants: its own, else the program's where its type passes that through, else
 * the one its data type implies.
 * @param {TargetLike} target
 * @param {string|null} programMetric
 * @returns {string|null} null when the type is a container, unknown, or absent
 */
function wantedMetric(target, programMetric) {
  if (inheritsProgramMetric(target, programMetric)) {
    return programMetric;
  }
  return target?.primaryMetric ?? TARGET_DATA_TYPE_META[target?.dataType]?.defaultMetric ?? null;
} // wantedMetric()

/**
 * @param {TargetLike[]} values
 * @returns {*} the one value they all share, or null if they do not share one
 */
function unanimous(values) {
  if (values.length === 0 || values.some((value) => value === null || value === undefined)) {
    return null;
  }
  return values.every((value) => value === values[0]) ? values[0] : null;
} // unanimous()

/**
 * Derive a program's recording defaults from its targets.
 *
 * Pass the program's OWN targets, not their sub-targets: a task analysis step is part of one skill
 * rather than a separate one, so a program of one task analysis is unanimous however many steps it
 * has.
 *
 * @param {TargetLike[]} targets
 * @param {string|null} [programMetric] - the metric the program already states, if any
 * @returns {{ primaryMetric: string|null, defaultTargetType: string|null,
 *   metricByTargetId: Object.<string, string> }} `primaryMetric` and `defaultTargetType` are null
 *   where the targets do not agree, meaning leave the program's alone. `metricByTargetId` carries
 *   the per-target metrics to write, and is empty whenever the metric was hoisted. It never names a
 *   target that inherits the program's metric: writing it would pin a value the target only reads.
 */
// eslint-disable-next-line import/prefer-default-export
export function deriveProgramDefaults(targets = [], programMetric = null) {
  const own = (targets ?? []).filter(Boolean);
  const sharedMetric = unanimous(own.map((target) => wantedMetric(target, programMetric)));
  const sharedDataType = unanimous(own.map((target) => target.dataType ?? null));

  return {
    primaryMetric: sharedMetric,
    defaultTargetType: sharedDataType,
    metricByTargetId: sharedMetric !== null
      ? {}
      : own.reduce((acc, target) => {
        const metric = wantedMetric(target, programMetric);
        return metric === null || inheritsProgramMetric(target, programMetric)
          ? acc
          : { ...acc, [target.id]: metric };
      }, {}),
  };
} // deriveProgramDefaults()
